import { useEffect } from "react";

/**
 * Sets unique <title>, <meta name="description">, <link rel="canonical">,
 * and the matching OpenGraph + Twitter cards on every public route. Fixes
 * the SEO scanner finding "Too many pages with identical titles /
 * descriptions" by giving each page its own metadata.
 *
 * Usage:
 *   useSeo({
 *     title: "Pricing · ARISSAI®",
 *     description: "Free, Starter, Pro and Enterprise plans for ARISSAI…",
 *     path: "/pricing",
 *   });
 */
export default function useSeo({ title, description, path, image }) {
  useEffect(() => {
    if (!title && !description) return;

    const prevTitle = document.title;
    if (title) document.title = title;

    /** Upsert a <meta name|property=...> tag. */
    const upsertMeta = (key, attr, value) => {
      if (!value) return null;
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      const prev = el.getAttribute("content");
      el.setAttribute("content", value);
      return { el, prev };
    };

    const restore = [];
    if (description) {
      restore.push(upsertMeta("description", "name", description));
      restore.push(upsertMeta("og:description", "property", description));
      restore.push(upsertMeta("twitter:description", "name", description));
    }
    if (title) {
      restore.push(upsertMeta("og:title", "property", title));
      restore.push(upsertMeta("twitter:title", "name", title));
    }

    // Canonical
    let canonicalEl = document.head.querySelector('link[rel="canonical"]');
    const prevCanonical = canonicalEl?.getAttribute("href") || null;
    if (path) {
      const fullUrl = `https://www.arissai.ca${path.startsWith("/") ? path : `/${path}`}`;
      if (!canonicalEl) {
        canonicalEl = document.createElement("link");
        canonicalEl.setAttribute("rel", "canonical");
        document.head.appendChild(canonicalEl);
      }
      canonicalEl.setAttribute("href", fullUrl);
      restore.push(upsertMeta("og:url", "property", fullUrl));
    }

    if (image) {
      restore.push(upsertMeta("og:image", "property", image));
      restore.push(upsertMeta("twitter:image", "name", image));
    }

    return () => {
      document.title = prevTitle;
      restore.forEach((r) => {
        if (r?.el && r.prev != null) r.el.setAttribute("content", r.prev);
      });
      if (canonicalEl && prevCanonical) canonicalEl.setAttribute("href", prevCanonical);
    };
  }, [title, description, path, image]);
}
